/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-this-in-sfc */
import { useQuery } from "@apollo/client";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import { LineBarPlotTypeSelector } from "components/charts/modern/inputs/PlotTypeSelector";
import TickerSymbolSelector from "components/charts/modern/inputs/TickerSymbolSelector";
import KeyValueTooltip from "components/charts/modern/tooltip/KeyValueTooltip";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import OnlyPremiumRender from "components/conversion/OnlyPremiumRender";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import { useMinWidthTailwindBreakpoints } from "components/ui/hooks/useTailwindBreakpoints";
import aeonik from "components/utils/aeonik";
import compareAsc from "date-fns/compareAsc";
import getYear from "date-fns/getYear";
import parse from "date-fns/parse";
import parseISO from "date-fns/parseISO";
import type Highcharts from "highcharts/highstock";
import type { SeriesBarOptions, SeriesLineOptions } from "highcharts/highstock";
import FreeFloatHistoryQuery from "lib/graphql/queries/FreeFloatHistoryQuery";
import type {
  IFreeFloatHistoryQuery,
  IFreeFloatHistoryQueryVariables,
} from "lib/graphql/queries/__generated__/FreeFloatHistoryQuery.generated";
import { TriangleAlertIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Fragment, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import useTheme from "components/ui/useTheme";
import classNames from "components/ui/classNames";

interface FreeFloatChartProps {
  tickerSymbols: string[];
  availableTickerSymbols?: string[];
  onToggleTickerSymbols?: (tickerSymbol: string[]) => void;
}

export default function FreeFloatChart({
  tickerSymbols,
  availableTickerSymbols,
  onToggleTickerSymbols,
}: FreeFloatChartProps) {
  const intl = useIntl();
  const theme = useTheme();
  const breakpoints = useMinWidthTailwindBreakpoints();
  const [plotType, setPlotType] = useState<"line" | "column">("line");

  // Theme-aware colors
  const slateOrDarkSlate = theme === "dark" ? colors.slate[800] : colors.slate[100];
  const labelsColor = theme === "dark" ? colors.slate[300] : colors.slate[500];
  const tooltipBgColor = theme === "dark" ? colors.slate[800] : colors.white;
  const tooltipBorderColor = theme === "dark" ? colors.slate[950] : colors.slate[200];
  const hoverBorderColor = theme === "dark" ? colors.slate[400] : colors.slate[500];
  const hoverFillColor = theme === "dark" ? colors.slate[700] : colors.slate[50];
  const lineColor = theme === "dark" ? colors.slate[400] : colors.slate[600];
  const markerFillColor = theme === "dark" ? colors.slate[900] : colors.slate[50];
  const barFillColor = theme === "dark" ? colors.slate[700] : colors.slate[100];
  const barBorderColor = theme === "dark" ? colors.slate[400] : colors.slate[600];
  const barHoverColor = theme === "dark" ? colors.slate[500] : colors.slate[700];
  const tickerSymbolsForSeries = [...tickerSymbols];
  const selectedTickerSymbol = tickerSymbolsForSeries[0];
  if (!selectedTickerSymbol) {
    throw new Error("Couldn't locate the selected ticker symbol");
  }
  const { data, previousData, loading } = useQuery<IFreeFloatHistoryQuery, IFreeFloatHistoryQueryVariables>(
    FreeFloatHistoryQuery,
    {
      variables: {
        tickerSymbol: tickerSymbols[0],
      },
    },
  );
  const freeFloatHistory = (loading && !data ? previousData?.freeFloatHistory : data?.freeFloatHistory) || [];

  const hasFreeFloatHistory = freeFloatHistory.length > 0;

  const sortedFreeFloatHistory = [...freeFloatHistory]
    .filter((node) => node.floatShareToTotalSharesOutstanding !== null && node.floatShareToTotalSharesOutstanding !== 0)
    .sort((a, b) => compareAsc(parseISO(a.asOfDate), parseISO(b.asOfDate)));
  const latestFreeFloat =
    sortedFreeFloatHistory[sortedFreeFloatHistory.length - 1]?.floatShareToTotalSharesOutstanding ?? null;

  // If the size is equal to 1, plot it as a single bar.
  const onlyPlotTypeBar = freeFloatHistory.length === 1;
  let plotTypeOption: "bar" | "line" = plotType === "line" ? "line" : "bar";

  if (onlyPlotTypeBar) {
    plotTypeOption = "bar";
  }

  const series: (SeriesLineOptions | SeriesBarOptions)[] = [
    {
      showInNavigator: true,
      type: plotTypeOption,
      animation: false,
      name: selectedTickerSymbol,
      data: freeFloatHistory
        .filter(
          (node) => node.floatShareToTotalSharesOutstanding !== null && node.floatShareToTotalSharesOutstanding !== 0,
        )
        .map((node) => ({
          x: getYear(parseISO(node.asOfDate)),
          y: node.floatShareToTotalSharesOutstanding,
          custom: node,
        }))
        .sort((a, b) => compareAsc(a.x, b.x)),
      custom: {
        tickerSymbol: selectedTickerSymbol,
        primaryColor: colors.sky[50],
        secondaryColor: colors.sky[600],
      },
    },
  ];

  const labels = {
    sharesOutstanding: intl.formatMessage({
      defaultMessage: "Ações em Circulação",
      id: "psT3CN",
    }),
    freeFloat: intl.formatMessage({
      defaultMessage: "Free Float",
      id: "bQN4OR",
    }),
  };
  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: slateOrDarkSlate,
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      labels: {
        format: "{value}",
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
    },
    yAxis: {
      type: "linear",
      // min: minValue,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      labels: {
        enabled: true,
        align: "center",
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
        formatter: function () {
          const percentageFormatter = new Intl.NumberFormat("en-US", {
            style: "percent",
            maximumFractionDigits: 2,
          });

          return percentageFormatter.format(Number(this.value));
        },
      },
      tickWidth: 0,
      showFirstLabel: true,
      showLastLabel: false,
      scrollbar: {
        enabled: false,
      },
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    series: [...series],
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      backgroundColor: tooltipBgColor,
      borderColor: tooltipBorderColor,
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        const node = (this.point as any).custom as (typeof freeFloatHistory)[number] | null | undefined;
        if (!this.key || !node || node.__typename !== "FreeFloatHistoryItem") {
          return "";
        }

        const percentageFormatter = new Intl.NumberFormat("en-US", {
          style: "percent",
          maximumFractionDigits: 2,
        });

        return renderToStaticMarkup(
          <KeyValueTooltip
            x={intl.formatDate(parse(node.asOfDate, "yyyy-MM-dd", new Date()), {
              year: "numeric",
              month: "numeric",
              day: "numeric",
            })}
            keyValues={[
              {
                key: labels.freeFloat,
                value: percentageFormatter.format(node.floatShareToTotalSharesOutstanding),
              },
            ]}
          />,
        );
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: hoverBorderColor,
            color: hoverFillColor,
            lineWidth: 2,
          },
        },
        dataLabels: {
          enabled: true,
          crop: false,
          style: {
            color: labelsColor,
            fontFamily: "novel-mono-pro-condensed",
            fontWeight: "600",
            fontSize: "10px",
            padding: "8px",
            textOutline: "none",
          },
          inside: false,
          formatter: function () {
            const node = (this.point as any).custom as (typeof freeFloatHistory)[number] | null | undefined;
            if (!this.key || !node || node.__typename !== "FreeFloatHistoryItem") {
              return "";
            }
            const percentageFormatter = new Intl.NumberFormat("en-US", {
              style: "percent",
              maximumFractionDigits: 2,
            });
            return percentageFormatter.format(node.floatShareToTotalSharesOutstanding);
          },
        },
      },
      line: {
        color: lineColor,
        lineWidth: 2,
        dataLabels: {
          enabled: true,
        },
        marker: {
          enabled: false,
          fillColor: markerFillColor,
          lineColor: lineColor,
          lineWidth: 4,
          width: 8,
          height: 8,
        },
      },
      bar: {
        color: barFillColor,
        borderWidth: 1.5,
        borderColor: barBorderColor,
        states: {
          hover: {
            color: barHoverColor,
            borderColor: barHoverColor,
          },
        },
        borderRadius: {
          where: "end",
          radius: 12,
          scope: "stack",
        },
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            callback: () => true,
          },
          chartOptions: {
            plotOptions: {
              column: {
                dataLabels: {
                  enabled: false,
                },
              },
            },
          },
        },
        {
          condition: {
            callback: () => {
              return breakpoints.lg || breakpoints.xl;
            },
          },
          chartOptions: {
            plotOptions: {
              column: {
                dataLabels: {
                  enabled: true,
                },
              },
            },
          },
        },
      ],
    },
  };

  if (loading && !hasFreeFloatHistory) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  return (
    <OnlyPremiumRender>
      <div className="flex h-full w-full flex-col space-y-4">
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex w-full flex-col items-center space-y-4 md:flex-row md:space-x-4 md:space-y-0">
            {onToggleTickerSymbols && availableTickerSymbols && availableTickerSymbols.length >= 2 && (
              <TickerSymbolSelector
                tickerSymbols={availableTickerSymbols}
                value={tickerSymbols}
                onChange={(tickerSymbols) => onToggleTickerSymbols(tickerSymbols)}
              />
            )}

            {!onlyPlotTypeBar && <LineBarPlotTypeSelector value={plotType} onChange={setPlotType} />}
          </div>
        </div>

        {hasFreeFloatHistory ? (
          <HighchartsChart options={options} className="h-96 min-h-0 xl:h-auto xl:flex-1" />
        ) : (
          <div className="flex h-96 w-80 items-center justify-center sm:w-full">
            <div className="space-y-2 text-center text-base tracking-wide text-gray-600 dark:text-gray-400">
              <div>
                <SadFaceIcon />
              </div>

              <p>
                <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
              </p>
            </div>
          </div>
        )}

        <FreeFloatTagAlongNotes latestFreeFloat={latestFreeFloat} />
      </div>
    </OnlyPremiumRender>
  );
}

function FreeFloatTagAlongNotes({ latestFreeFloat }: { latestFreeFloat: number | null }) {
  const intl = useIntl();
  const issuer = useRootIssuer();

  // Conceitos específicos da B3 (Novo Mercado, Tag Along), só fazem sentido para empresas brasileiras.
  if (issuer.__typename !== "BrazilianCompanyIssuer") {
    return null;
  }

  const { tagAlong } = issuer;

  const percentFormatter = new Intl.NumberFormat(intl.locale, {
    style: "percent",
    maximumFractionDigits: 1,
  });

  const emphasis = (chunks: ReactNode) => (
    <span className="font-bold text-slate-700 dark:text-slate-200">{chunks}</span>
  );

  return (
    <div className="flex shrink-0 flex-col divide-y divide-slate-100 rounded-xl border border-slate-100 bg-slate-50/60 dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-800/40">
      {/* Free Float */}
      <div className="flex flex-col gap-2 p-4">
        <div className="flex flex-col gap-1.5">
          <span className="whitespace-nowrap text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <FormattedMessage defaultMessage="Free Float" id="bQN4OR" />
          </span>
          {latestFreeFloat !== null && (
            <span
              className={classNames("text-xl font-bold tabular-nums leading-none", {
                "text-emerald-600 dark:text-emerald-400": latestFreeFloat >= 0.25,
                "text-amber-600 dark:text-amber-500": latestFreeFloat < 0.25,
              })}
            >
              {percentFormatter.format(latestFreeFloat)}
            </span>
          )}
        </div>
        <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
          <FormattedMessage
            defaultMessage="<b>25%</b> é o mínimo de free float exigido para empresas do Novo Mercado, segmento com as regras de governança mais rígidas da B3. Um free float abaixo disso pode indicar menor liquidez e maior concentração acionária."
            description="Explicação sobre free float abaixo do gráfico"
            id="M2WHN1"
            values={{ b: emphasis }}
          />
        </p>
      </div>

      {/* Tag Along */}
      <div className="flex flex-col gap-2 p-4">
        <div className="flex flex-col gap-1.5">
          <span className="whitespace-nowrap text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Tag Along
          </span>
          {tagAlong && (
            <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 leading-none">
              {(
                [
                  ["ON", tagAlong.commonStock],
                  ["PN", tagAlong.preferredStock],
                  ["UNIT", tagAlong.unitStock],
                ] as const
              )
                .filter(([, value]) => value !== null)
                .map(([label, value], index) => (
                  <Fragment key={label}>
                    {index > 0 && <span className="text-slate-300 dark:text-slate-600">/</span>}
                    <span
                      className="text-xl font-bold tabular-nums"
                      style={{ color: getColorByPercentage(value as number) }}
                    >
                      {value}% <span className="text-sm text-slate-500 dark:text-slate-200">{label}</span>
                    </span>
                  </Fragment>
                ))}
              {tagAlong?.preferredStock === 0 && (
                <span className="inline-flex w-fit items-center gap-1.5 rounded-md bg-red-50 px-2 py-1 text-xs font-semibold text-red-600 dark:bg-red-950/40 dark:text-red-400">
                  <TriangleAlertIcon className="size-3.5 shrink-0" strokeWidth={2.5} />
                  <FormattedMessage
                    defaultMessage="PN sem Tag Along"
                    description="Aviso quando a empresa não oferece tag along nas ações PN"
                    id="TLgE6d"
                  />
                </span>
              )}
            </div>
          )}
        </div>
        <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
          <FormattedMessage
            defaultMessage="É o direito de vender suas ações pelo mesmo preço pago ao controlador quando a empresa troca de dono. A lei garante <b>80%</b> para ações ON; o Novo Mercado exige <b>100%</b>. Quanto maior o percentual, mais protegido o minoritário. <b>Cuidado com empresas que não têm Tag Along na PN.</b>"
            description="Explicação sobre tag along abaixo do gráfico"
            id="tDO3IG"
            values={{ b: emphasis }}
          />
        </p>
      </div>
    </div>
  );
}

function getColorByPercentage(tagAlongPercentage: number): string {
  if (tagAlongPercentage === 100) {
    return colors.emerald[500];
  }

  if (tagAlongPercentage >= 80 && tagAlongPercentage < 100) {
    return colors.amber[600];
  }

  return colors.red[500];
}
